import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Briefcase, Calendar, Clock, Layers, Download, Star } from "lucide-react";
import { api, API } from "../lib/api";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import Seo from "../components/site/Seo";
import { resolveImageUrl } from "../components/admin/ImageUpload";

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${id}`);
        setProject(data);
        setActiveImg(0);
      } catch {
        setNotFound(true);
        return;
      }
      try {
        const { data } = await api.get("/projects");
        setRelated(data.filter((p) => p.id !== id).slice(0, 3));
      } catch { /* ignore */ }
    })();
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Seo title="Project not found" />
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center">
          <div className="section-overline justify-center inline-flex">Projects</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold mt-4 text-[#0F172A]">Project not found</h1>
          <Link to="/" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#2563EB] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wide hover:bg-[#1D4ED8]">
            <ArrowLeft size={16} /> Back to home
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!project) {
    return <div className="min-h-screen grid place-items-center text-[#475569]">Loading...</div>;
  }

  const cover = resolveImageUrl(project.image_url);
  const gallery = (project.gallery && project.gallery.length ? project.gallery : [project.image_url]).filter(Boolean);
  const activeUrl = resolveImageUrl(gallery[activeImg] || project.image_url);

  return (
    <div data-testid="project-detail-page" className="bg-white">
      <Seo title={project.name} description={project.summary} image={cover} type="article" path={`/projects/${project.id}`} />
      <Navbar />

      <main>
        <section className="relative pt-40 pb-20 bg-azure-mesh overflow-hidden">
          <div className="absolute inset-0 bg-grid-blue opacity-50" />
          <div className="relative max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <Link to="/#projects" data-testid="project-back" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#2563EB]">
                <ArrowLeft size={14} /> Back to Projects
              </Link>
              <div className="mt-6 section-overline">{project.category}</div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold mt-4 leading-[1.02] text-balance text-[#0F172A]">
                {project.name}
                {project.featured && (
                  <span className="ml-3 inline-flex items-center gap-1 align-middle px-3 py-1.5 rounded-full bg-[#2563EB] text-white text-xs font-semibold uppercase tracking-wider" data-testid="detail-featured-badge">
                    <Star size={12} fill="currentColor" /> Featured
                  </span>
                )}
              </h1>
              <p className="mt-6 text-xl text-[#475569] max-w-2xl leading-relaxed">{project.summary}</p>
              {project.tags?.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {project.tags.map((t) => (
                    <Link key={t} to={`/?tag=${encodeURIComponent(t)}#projects`} className="px-3 py-1 rounded-md bg-blue-50 text-[#1D4ED8] text-xs font-semibold hover:bg-blue-100 transition-colors">
                      #{t}
                    </Link>
                  ))}
                </div>
              )}
              {project.metrics?.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {project.metrics.map((m) => (
                    <span key={m} className="px-4 py-2 rounded-full bg-[#2563EB] text-white text-sm font-semibold shadow-[0_12px_28px_-12px_rgba(37,99,235,0.55)]">{m}</span>
                  ))}
                </div>
              )}
              <a
                href={`${API}/projects/${project.id}/case-study.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-case-study"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#1E3A8A] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wide shadow-[0_18px_40px_-18px_rgba(15,23,42,0.5)] transition-colors"
              >
                <Download size={16} /> Download Case Study (PDF)
              </a>
            </div>

            {/* Project meta sidebar */}
            <aside className="lg:col-span-5 grid grid-cols-2 gap-3">
              <MetaCard icon={Briefcase} label="Client" value={project.client || "Multiple"} />
              <MetaCard icon={Calendar} label="Year" value={project.year || "—"} />
              <MetaCard icon={Clock} label="Duration" value={project.duration || "—"} />
              <MetaCard icon={Layers} label="Category" value={project.category} />
            </aside>
          </div>
        </section>

        {/* Image gallery */}
        {gallery.length > 0 && (
          <div className="max-w-6xl mx-auto px-6 md:px-12 -mt-16 relative z-10">
            <div className="rounded-2xl overflow-hidden border border-blue-100 shadow-[0_30px_80px_-40px_rgba(37,99,235,0.4)] bg-white p-3">
              <div className="rounded-xl overflow-hidden aspect-[16/9] bg-blue-50">
                <img key={activeUrl} src={activeUrl} alt={project.name} className="w-full h-full object-cover" />
              </div>
              {gallery.length > 1 && (
                <div className="mt-3 grid grid-cols-3 gap-3">
                  {gallery.map((g, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      data-testid={`gallery-thumb-${i}`}
                      className={`aspect-[16/10] rounded-md overflow-hidden border-2 transition-all ${i === activeImg ? "border-[#2563EB]" : "border-transparent hover:border-blue-200"}`}
                    >
                      <img src={resolveImageUrl(g)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24 grid md:grid-cols-3 gap-10">
          <Block label="Problem" body={project.problem} />
          <Block label="Solution" body={project.solution} />
          <Block label="Impact" body={project.impact} />
        </section>

        {project.tech_stack?.length > 0 && (
          <section className="bg-mist py-16">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
              <div className="section-overline">Technology Stack</div>
              <h2 className="mt-4 font-display text-3xl font-semibold text-[#0F172A]">Built with</h2>
              <div className="mt-6 flex flex-wrap gap-3">
                {project.tech_stack.map((t) => (
                  <span key={t} className="px-4 py-2.5 rounded-full bg-white border border-blue-200 text-[#1E3A8A] text-sm font-semibold inline-flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-[#2563EB]" /> {t}
                  </span>
                ))}
              </div>
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="bg-soft-blue py-20">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="section-overline">Related</div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold mt-4 text-[#0F172A]">More Programmes</h2>
              <div className="mt-10 grid md:grid-cols-3 gap-6">
                {related.map((p) => (
                  <Link key={p.id} to={`/projects/${p.id}`} className="group bg-white border border-blue-100 rounded-xl overflow-hidden hover:border-[#2563EB] hover:shadow-lg transition-all">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={resolveImageUrl(p.image_url)} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-5">
                      <div className="overline">{p.category}</div>
                      <div className="mt-2 font-display text-lg text-[#0F172A] line-clamp-2">{p.name}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

const MetaCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-lg p-4 border border-blue-100">
    <div className="flex items-center gap-2 text-xs text-[#475569] uppercase tracking-[0.18em] font-semibold">
      <Icon size={12} className="text-[#2563EB]" /> {label}
    </div>
    <div className="mt-2 text-sm font-display text-[#0F172A] leading-snug">{value}</div>
  </div>
);

const Block = ({ label, body }) => (
  <div className="border-l-4 border-[#2563EB] pl-5">
    <div className="overline">{label}</div>
    <p className="mt-3 text-[#475569] leading-relaxed">{body || "—"}</p>
    <div className="mt-4">
      <CheckCircle2 size={16} className="text-[#2563EB]" />
    </div>
  </div>
);
